import React, { ReactElement, useState } from 'react';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Mail, Trash2, UserPlus, X } from 'lucide-react';

import { Button } from '@ui/components/ui/button';
import { Input } from '@ui/components/ui/input';
import { MultiSelectField } from '@ui/components/ui/multi-select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/ui/tooltip';

import { ClientAccessRole } from '@data';
import { isValidEmail } from '@data/utils/validation';

import ConfirmDialog from '../../ConfirmDialog';

/** Somebody the guided add will create once the client exists. Local until then, hence the local id. */
export interface DraftPerson {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: ClientAccessRole;
  /** The client's own top account. At most one, which is what the API accepts. */
  isSuperAdmin?: boolean;
  productIds: string[];
}

/** The role as the form asks it: the super admin is a third choice rather than a tick beside admin. */
type FormRole = 'super' | ClientAccessRole;

const GROUPS: { role: ClientAccessRole; titleKey: string }[] = [
  { role: ClientAccessRole.admin, titleKey: 'access.admins' },
  { role: ClientAccessRole.read, titleKey: 'access.viewOnly' },
];

const displayName = (person: DraftPerson): string =>
  `${person.firstName ?? ''} ${person.lastName ?? ''}`.trim() || person.email;

const roleOf = (person: DraftPerson): FormRole => (person.isSuperAdmin ? 'super' : person.role);

// appearance-none with an overlaid chevron, so the arrow sits the same distance from the corner as the
// multi-select's own (px-3) rather than at the browser's default inset.
const selectBase =
  'h-8 appearance-none rounded-lg border pl-2.5 pr-8 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50';
const selectDefault = 'border-input bg-popover text-foreground';
// Tinted, so a super admin reads at a glance without a badge beside the select.
const selectSuper = 'border-primary/20 bg-primary/10 font-medium text-primary';

/** Exported for the prompt that follows a send: the same standings, asked the same way. */
export const RoleSelect = ({
  value,
  onChange,
  ariaLabel,
  offerSuper,
  className,
}: {
  value: FormRole;
  onChange: (value: FormRole) => void;
  ariaLabel: string;
  offerSuper: boolean;
  className?: string;
}): ReactElement => {
  const { t } = useTranslation();
  const isSuper = value === 'super';
  return (
    <div className={cx('relative', className)}>
      <select
        aria-label={ariaLabel}
        value={value}
        onChange={event => onChange(event.target.value as FormRole)}
        className={cx(selectBase, 'w-full', isSuper ? selectSuper : selectDefault)}
      >
        {/* Offered only while nobody holds it: two super admins could each remove the other, and the API
            refuses the second for that reason. */}
        {(offerSuper || isSuper) && <option value="super">{t('access.superAdmin')}</option>}
        <option value={ClientAccessRole.admin}>{t('access.admin')}</option>
        <option value={ClientAccessRole.read}>{t('access.viewOnly')}</option>
      </select>
      <ChevronDown
        aria-hidden
        className={cx(
          'pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2',
          isSuper ? 'text-primary' : 'text-muted-foreground',
        )}
      />
    </div>
  );
};

/**
 * The people who will read one client's reports, while the client is still being described.
 *
 * The same panel the client's own page shows, over a draft: nothing here is written until the guided add
 * finishes, so a row can still be re-roled or dropped. What each person may read is picked from the
 * client's own products, and everybody starts able to read all of them — the common case is a client
 * seeing the work we do for them, and narrowing it is the exception worth a click.
 */
const ClientAccessPanel = ({
  products,
  people,
  onChange,
}: {
  /** This client's products. In the guided add that is the one being created, or none. */
  products: { id: string; name: string }[];
  people: DraftPerson[];
  onChange: (next: DraftPerson[]) => void;
}): ReactElement => {
  const { t } = useTranslation();

  const [adding, setAdding] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<FormRole>(ClientAccessRole.read);
  // Everything this client has, from the first render as well as from the button below: the panel opens
  // straight on the form when nobody has been added, and that first person wants the same default.
  const [productIds, setProductIds] = useState<string[]>(() => products.map(product => product.id));
  const [confirmRemove, setConfirmRemove] = useState<DraftPerson | null>(null);

  const options = products.map(product => ({ value: product.id, label: product.name }));
  const allIds = products.map(product => product.id);
  const superTaken = people.some(person => person.isSuperAdmin);

  const openForm = (): void => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setRole(ClientAccessRole.read);
    // Everything this client has, which is what somebody at the client is normally there to read.
    setProductIds(allIds);
    setAdding(true);
  };

  const trimmedEmail = email.trim().toLowerCase();
  const duplicate = !!trimmedEmail && people.some(person => person.email.toLowerCase() === trimmedEmail);
  const error = duplicate ? t('access.duplicatePerson') : null;
  // A name is asked for but not insisted on here: the address is what makes the account.
  const canAdd = isValidEmail(email) && !duplicate && !!firstName.trim();

  const add = (): void => {
    if (!canAdd) return;
    const isSuper = role === 'super';
    onChange([
      ...people,
      {
        id: crypto.randomUUID(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        role: isSuper ? ClientAccessRole.admin : role,
        ...(isSuper ? { isSuperAdmin: true } : {}),
        productIds: productIds.filter(id => allIds.includes(id)),
      },
    ]);
    setAdding(false);
  };

  const changeRole = (id: string, next: FormRole): void =>
    onChange(
      people.map(person =>
        person.id === id
          ? {
              ...person,
              role: next === ClientAccessRole.read ? ClientAccessRole.read : ClientAccessRole.admin,
              isSuperAdmin: next === 'super' || undefined,
            }
          : person,
      ),
    );

  const setProducts = (id: string, next: string[]): void =>
    onChange(people.map(person => (person.id === id ? { ...person, productIds: next } : person)));

  /** Whether a row holds the client's whole list, which is what makes it read "All products". */
  const holdsEverything = (ids: string[]): boolean => allIds.length > 0 && ids.length === allIds.length;

  // What a person may read, when their row is not being edited: everything, nothing, or the first with
  // the rest behind a tooltip — the same shorthand the clients table uses.
  const renderScope = (person: DraftPerson): ReactElement => {
    if (holdsEverything(person.productIds))
      return <span className="text-xs text-muted-foreground">{t('allProducts')}</span>;
    const names = person.productIds
      .map(id => products.find(product => product.id === id)?.name)
      .filter(Boolean) as string[];
    if (!names.length) return <span className="text-xs text-muted-foreground">{t('noProducts')}</span>;
    const [first, ...rest] = names;
    if (!rest.length) return <span className="text-xs text-muted-foreground">{first}</span>;
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="cursor-default text-xs text-muted-foreground">
            {first} <span className="font-bold text-foreground">{t('nMore', { count: rest.length })}</span>
          </span>
        </TooltipTrigger>
        <TooltipContent>{names.join(', ')}</TooltipContent>
      </Tooltip>
    );
  };

  const row = (person: DraftPerson): ReactElement => {
    const name = displayName(person);
    return (
      <div
        key={person.id}
        className="flex items-center gap-3 border-b border-border py-2.5 duration-200 ease-out animate-in fade-in-0 slide-in-from-top-2 last:border-b-0"
      >
        <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
          <Mail className="size-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-foreground">{name}</p>
          {name !== person.email && <p className="truncate text-xs text-muted-foreground">{person.email}</p>}
        </div>
        <div className="flex items-center gap-3">
          {options.length > 0 ? (
            <MultiSelectField
              label={<span className="sr-only">{t('a11y.productsPersonCanView', { email: person.email })}</span>}
              options={options}
              value={person.productIds}
              onChange={ids => setProducts(person.id, ids)}
              allSelected={holdsEverything(person.productIds)}
              onToggleAll={next => setProducts(person.id, next ? allIds : [])}
              emptyLabel={t('noProducts')}
              summaryThreshold={0}
              renderCountSummary={count =>
                holdsEverything(person.productIds) ? t('allProducts') : t('access.nProducts', { count })
              }
              selectAllLabel={t('allProducts')}
              className="w-36 shrink-0 gap-0"
            />
          ) : (
            <span className="shrink-0 text-right">{renderScope(person)}</span>
          )}
          <RoleSelect
            ariaLabel={t('a11y.roleFor', { email: person.email })}
            className="w-32 shrink-0"
            offerSuper={!superTaken}
            value={roleOf(person)}
            onChange={next => changeRole(person.id, next)}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => setConfirmRemove(person)}
            aria-label={`${t('actions.remove')} ${person.email}`}
            className="shrink-0 text-muted-foreground hover:text-foreground"
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      </div>
    );
  };

  // With nobody added yet the form is the panel — a button whose only job is to reveal the one thing
  // this step is for is a click asking to be skipped.
  const empty = people.length === 0;
  const showForm = adding || empty;

  return (
    <div>
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="text-lg font-bold text-foreground">{t('access.peopleWithAccess')}</h2>
        {!empty && !adding && (
          <Button variant="outline" size="sm" onClick={openForm}>
            <UserPlus className="size-4" />
            {t('addPerson')}
          </Button>
        )}
      </div>

      {showForm && (
        <form
          onSubmit={event => {
            event.preventDefault();
            add();
          }}
          className="mb-4 space-y-3 rounded-lg border border-border bg-muted/30 p-3 duration-200 animate-in fade-in-0"
        >
          {!empty && (
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-foreground">{t('addPerson')}</p>
              <button
                type="button"
                onClick={() => setAdding(false)}
                aria-label={t('actions.cancel')}
                className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <X className="size-4" />
              </button>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Input
              value={firstName}
              onChange={event => setFirstName(event.target.value)}
              placeholder={t('firstName')}
              aria-label={t('firstName')}
            />
            <Input
              value={lastName}
              onChange={event => setLastName(event.target.value)}
              placeholder={t('lastName')}
              aria-label={t('lastName')}
            />
          </div>
          <div>
            <Input
              type="email"
              value={email}
              onChange={event => setEmail(event.target.value)}
              placeholder="name@example.com"
              aria-label={t('titles.email')}
              aria-invalid={Boolean(error)}
            />
            {error && <p className="mt-1 text-sm text-destructive">{error}</p>}
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1 text-sm font-medium text-foreground">
              {t('role')}
              <RoleSelect
                ariaLabel={t('role')}
                className="w-32"
                offerSuper={!superTaken}
                value={role}
                onChange={setRole}
              />
            </div>
            <div className="flex flex-1 flex-col gap-1 text-sm font-medium text-foreground">
              {t('access.canView')}
              <MultiSelectField
                label={<span className="sr-only">{t('a11y.productsNewPersonCanView')}</span>}
                options={options}
                value={productIds}
                onChange={setProductIds}
                allSelected={holdsEverything(productIds)}
                onToggleAll={next => setProductIds(next ? allIds : [])}
                emptyLabel={t('noProducts')}
                summaryThreshold={0}
                renderCountSummary={count =>
                  holdsEverything(productIds) ? t('allProducts') : t('access.nProducts', { count })
                }
                selectAllLabel={t('allProducts')}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            {!empty && (
              <Button type="button" variant="ghost" onClick={() => setAdding(false)}>
                {t('actions.cancel')}
              </Button>
            )}
            <Button type="submit" disabled={!canAdd}>
              <UserPlus className="size-4" />
              {t('actions.add')}
            </Button>
          </div>
        </form>
      )}

      {!empty && (
        <div className="space-y-4">
          {GROUPS.map(group => {
            const inGroup = people.filter(person => person.role === group.role);
            if (!inGroup.length) return null;
            return (
              <section key={group.role}>
                <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {t(group.titleKey)}
                </p>
                <div>{inGroup.map(row)}</div>
              </section>
            );
          })}
        </div>
      )}

      <ConfirmDialog
        open={confirmRemove !== null}
        onOpenChange={next => !next && setConfirmRemove(null)}
        title={t('confirmRemoveTitle', { name: confirmRemove ? displayName(confirmRemove) : '' })}
        description={t('removeAccessDescription')}
        confirmLabel={t('actions.remove')}
        onConfirm={() => {
          if (confirmRemove) onChange(people.filter(person => person.id !== confirmRemove.id));
          setConfirmRemove(null);
        }}
      />
    </div>
  );
};

export default ClientAccessPanel;
